/**
 * Admin Sales Report — revenue, units, AOV, by-day chart, top categories & products.
 *
 * Backed by GET /api/admin/reports/sales?days=N.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import {
    Loader2,
    TrendingUp,
    ShoppingBag,
    Package,
    BadgeIndianRupee,
} from "lucide-react";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    BarChart,
    Bar,
    Cell,
} from "recharts";
import { ExportButtons } from "@/lib/exportData";

const RANGE_OPTIONS = [
    { value: 7, label: "Last 7 days" },
    { value: 14, label: "Last 14 days" },
    { value: 30, label: "Last 30 days" },
    { value: 60, label: "Last 60 days" },
    { value: 90, label: "Last 90 days" },
    { value: 180, label: "Last 6 months" },
    { value: 365, label: "Last 12 months" },
];

const PALETTE = ["#1B1B1F", "#6E7B5B", "#C07A5A", "#A8B3C2", "#D8C3A5", "#3D3934"];

function Stat({ icon: Icon, label, value, sub }) {
    return (
        <div className="rounded-2xl border border-[#E7DED2] bg-white p-5">
            <div className="flex items-center gap-2 text-[10px] font-mono-ui tracking-[0.18em] uppercase text-[#857C72]">
                <Icon className="h-3.5 w-3.5" /> {label}
            </div>
            <p className="font-display text-3xl mt-2">{value}</p>
            {sub && <p className="text-xs text-[#857C72] mt-1">{sub}</p>}
        </div>
    );
}

export default function AdminReportsSales() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/reports/sales?days=${days}`)
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    }, [days]);

    const byDayChart = useMemo(() => {
        if (!data) return [];
        return (data.by_day || []).map((d) => ({
            date: d.date.slice(5),
            revenue: d.revenue / 100,
            orders: d.orders,
            units: d.units,
        }));
    }, [data]);

    return (
        <div className="space-y-6" data-testid="admin-reports-sales">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[#857C72]">Reports</p>
                    <h1 className="font-display text-3xl sm:text-4xl mt-1">Sales report</h1>
                    <p className="text-xs text-[#857C72] mt-1">
                        Revenue, orders, and best-sellers across paid orders.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={days}
                        onChange={(e) => setDays(parseInt(e.target.value, 10))}
                        className="px-4 py-2 rounded-full bg-white border border-[#E7DED2] text-xs font-mono-ui"
                        data-testid="sales-range"
                    >
                        {RANGE_OPTIONS.map((r) => (
                            <option key={r.value} value={r.value}>
                                {r.label}
                            </option>
                        ))}
                    </select>
                    {data && (
                        <ExportButtons
                            rows={data.by_product || []}
                            filename={`xplosiv_event_revenue_top_packages_${days}d`}
                            columns={["title", "category", "units", "revenue"]}
                        />
                    )}
                </div>
            </header>

            {loading || !data ? (
                <div className="flex items-center justify-center py-16 text-[#857C72]">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading sales data…
                </div>
            ) : (
                <>
                    <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <Stat
                            icon={BadgeIndianRupee}
                            label="Revenue"
                            value={formatPrice(data.total_revenue || 0)}
                            sub={`across ${data.total_orders} paid order${data.total_orders === 1 ? "" : "s"}`}
                        />
                        <Stat
                            icon={ShoppingBag}
                            label="Orders"
                            value={data.total_orders}
                            sub={`${days}-day window`}
                        />
                        <Stat
                            icon={Package}
                            label="Units sold"
                            value={data.units_sold}
                        />
                        <Stat
                            icon={TrendingUp}
                            label="Avg order value"
                            value={formatPrice(data.aov || 0)}
                        />
                    </section>

                    <section className="rounded-2xl border border-[#E7DED2] bg-white p-5">
                        <div className="flex items-center justify-between">
                            <p className="font-display text-xl">Revenue by day</p>
                            <p className="text-[10px] font-mono-ui text-[#857C72] uppercase tracking-[0.12em]">
                                {byDayChart.length} day{byDayChart.length === 1 ? "" : "s"} with sales
                            </p>
                        </div>
                        <div className="h-72 mt-4">
                            {byDayChart.length === 0 ? (
                                <div className="h-full flex items-center justify-center text-xs text-[#857C72]">
                                    No paid orders in this range.
                                </div>
                            ) : (
                                <ResponsiveContainer>
                                    <LineChart data={byDayChart}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#E7DED2" />
                                        <XAxis dataKey="date" stroke="#857C72" fontSize={11} />
                                        <YAxis
                                            stroke="#857C72"
                                            fontSize={11}
                                            tickFormatter={(v) => `\u20B9${v.toLocaleString("en-IN")}`}
                                        />
                                        <Tooltip
                                            formatter={(value, name) =>
                                                name === "revenue" ? [`\u20B9${value.toLocaleString("en-IN")}`, "Revenue"] : [value, name]
                                            }
                                            contentStyle={{ borderRadius: 12 }}
                                        />
                                        <Line type="monotone" dataKey="revenue" stroke="#1B1B1F" strokeWidth={2.5} dot={{ r: 3 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </section>

                    <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="rounded-2xl border border-[#E7DED2] bg-white p-5">
                            <div className="flex items-center justify-between">
                                <p className="font-display text-xl">Revenue by category</p>
                                <ExportButtons
                                    rows={data.by_category || []}
                                    filename={`xplosiv_event_revenue_by_type_${days}d`}
                                    columns={["category", "revenue", "units"]}
                                />
                            </div>
                            <div className="h-64 mt-4">
                                {(data.by_category || []).length === 0 ? (
                                    <div className="h-full flex items-center justify-center text-xs text-[#857C72]">
                                        No category data.
                                    </div>
                                ) : (
                                    <ResponsiveContainer>
                                        <BarChart
                                            data={(data.by_category || []).map((c) => ({ ...c, revenueInr: c.revenue / 100 }))}
                                            layout="vertical"
                                            margin={{ left: 24 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="#E7DED2" />
                                            <XAxis
                                                type="number"
                                                stroke="#857C72"
                                                fontSize={11}
                                                tickFormatter={(v) => `\u20B9${v.toLocaleString("en-IN")}`}
                                            />
                                            <YAxis type="category" dataKey="category" stroke="#857C72" fontSize={11} width={90} />
                                            <Tooltip
                                                formatter={(v) => `\u20B9${Number(v).toLocaleString("en-IN")}`}
                                                contentStyle={{ borderRadius: 12 }}
                                            />
                                            <Bar dataKey="revenueInr" radius={[0, 8, 8, 0]}>
                                                {(data.by_category || []).map((_, i) => (
                                                    <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </div>

                        <div className="rounded-2xl border border-[#E7DED2] bg-white p-5">
                            <p className="font-display text-xl">Top products</p>
                            <div className="mt-3 divide-y divide-[#F4EFE6] max-h-72 overflow-y-auto">
                                {(data.by_product || []).slice(0, 12).map((p, i) => (
                                    <div key={p.product_id || i} className="py-2.5 flex items-center gap-3" data-testid={`top-product-${i}`}>
                                        <span className="text-xs font-mono-ui text-[#857C72] w-5">{i + 1}.</span>
                                        {p.image ? (
                                            <img src={p.image} alt="" className="h-9 w-9 rounded-lg object-cover bg-[#F4EFE6]" />
                                        ) : (
                                            <div className="h-9 w-9 rounded-lg bg-[#F4EFE6]" />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium truncate">{p.title}</p>
                                            <p className="text-[10px] font-mono-ui text-[#857C72]">
                                                {p.category} · {p.units} units
                                            </p>
                                        </div>
                                        <p className="text-xs font-display">{formatPrice(p.revenue)}</p>
                                    </div>
                                ))}
                                {(data.by_product || []).length === 0 && (
                                    <div className="py-8 text-center text-xs text-[#857C72]">No sales yet.</div>
                                )}
                            </div>
                        </div>
                    </section>

                    <section className="rounded-2xl border border-[#E7DED2] bg-white p-5">
                        <p className="font-display text-xl">Order status breakdown</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {(data.by_status || []).map((s) => (
                                <div
                                    key={s.status}
                                    className="px-3 py-1.5 rounded-full border border-[#E7DED2] bg-[#FFFCF7] text-xs font-mono-ui flex items-center gap-2"
                                >
                                    <span className="uppercase tracking-[0.12em] text-[#857C72]">{s.status.replace(/_/g, " ")}</span>
                                    <span className="font-display">{s.count}</span>
                                </div>
                            ))}
                            {(data.by_status || []).length === 0 && (
                                <p className="text-xs text-[#857C72]">No order activity in this window.</p>
                            )}
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}
